import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { ShieldCheck, ShieldAlert, Loader2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const toneClasses = {
  strong: "border-emerald-300/25 bg-emerald-300/10 text-emerald-200",
  good: "border-cyan-300/25 bg-cyan-300/10 text-cyan-200",
  mixed: "border-amber-300/25 bg-amber-300/10 text-amber-200",
  low: "border-red-300/25 bg-red-300/10 text-red-200",
};

export default function ForecastTrustScore({ lat, lon }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchTrustScore = useCallback(async () => {
    if (!lat || !lon) return;
    setLoading(true);
    try {
      const response = await axios.get(`${API}/api/weather/enhanced/trust-score`, { params: { lat, lon }, timeout: 12000 });
      setData(response.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [lat, lon]);

  useEffect(() => {
    fetchTrustScore();
  }, [fetchTrustScore]);

  if (loading && !data) {
    return (
      <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] px-3 py-1.5 text-xs text-white/45" data-testid="forecast-trust-score-loading">
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        Checking trust score
      </div>
    );
  }

  if (!data) return null;

  const Icon = data.score >= 70 ? ShieldCheck : ShieldAlert;
  const tone = toneClasses[data.tone] || toneClasses.mixed;

  return (
    <div className={`inline-flex max-w-full items-center gap-2 rounded-full border px-3 py-1.5 text-xs shadow-sm ${tone}`} data-testid="forecast-trust-score-badge">
      <Icon className="h-3.5 w-3.5 shrink-0" />
      <span className="font-black" data-testid="forecast-trust-score-value">{data.score}%</span>
      <span className="font-semibold" data-testid="forecast-trust-score-label">{data.label} trust</span>
      {data.factors?.[0] && <span className="hidden max-w-[280px] truncate opacity-70 sm:inline" data-testid="forecast-trust-score-factor">{data.factors[0]}</span>}
    </div>
  );
}